import AnimationClip, { AnimationClipEvent } from '../AnimationClip';
import GameObject from '../../../GameObject/GameObject';
import Armature from 'Prototype/Armature';
import { PlayAnimation } from '../AnimationHelper';
import { DragonbonesDefault } from './DragonbonesDefault';
import Point from 'Utility/Point';
import DragonbonesComponent from 'Component/Dragonbones';
import dragonBones from 'pixi-dragonbones';
import PlinkoToken, {
    getTokenBounceAnimationName,
    getTokenLandAnimationName,
    PlinkoPathValue,
} from 'Prefabs/Plinko/PlinkoToken';
import { Container } from 'pixi.js';
import { setDropperAnimation } from 'System/Plinko/Handler/PlinkoMiniGameHandler';
import { delay } from 'Utility/Task/TaskQueue';

interface IDropPlinkoTokenMeta {
    dropperArmature: Armature;
    plinkoToken: PlinkoToken;
    tokenContainer: GameObject<Container>;
    path: PlinkoPathValue[];
    startIndex: number;
    dropAnimation: string;
}

const firstDropAnimations = [
    {
        startIndex: 0,
        time: 0.1,
        animation: 'first_drop_right_1',
        startX: -86,
        startY: -213,
    },
    {
        startIndex: 0,
        time: 0.5,
        animation: 'first_drop_left_4',
        startX: -60,
        startY: -213,
    },
    {
        startIndex: 0,
        time: 0.83,
        animation: 'first_drop_left_2',
        startX: -43,
        startY: -213,
    },

    {
        startIndex: 1,
        time: 1.4,
        animation: 'first_drop_left_3',
        startX: 29,
        startY: -213,
    },

    {
        startIndex: 1,
        time: 2.5,
        animation: 'first_drop_left_1',
        startX: 81,
        startY: -213,
    },
    {
        startIndex: 1,
        time: 3.17,
        animation: 'first_drop_right_4',
        startX: 50,
        startY: -213,
    },
    {
        startIndex: 1,
        time: 3.5,
        animation: 'first_drop_right_2',
        startX: 38,
        startY: -213,
    },

    {
        startIndex: 0,
        time: 4.2,
        animation: 'first_drop_right_3',
        startX: -33,
        startY: -213,
    },
];

/**
 * Get the next drop animation based on the given progress. We will wait for the returned progress value to start the
 * initial drop to make it look natural.
 * @param time Current dropper idle animation progress 0-1.
 */
export function getNextDropAnimation(time: number) {
    const anim = firstDropAnimations.find(anim => anim.time >= time);

    // Wrap back to first animation.
    if (anim === undefined) return firstDropAnimations[0];

    return anim;
}

export class DropPlinkoToken extends AnimationClip<IDropPlinkoTokenMeta> {
    private plinkoToken: GameObject;
    private delta: Point;
    private tokenPreviousPosition: Point;
    private bone: dragonBones.Bone;

    public *execute(gameObject: GameObject, meta?: IDropPlinkoTokenMeta): IterableIterator<number> {
        const { dropperArmature, plinkoToken, path, tokenContainer, dropAnimation } = meta;

        const tokenArmature = plinkoToken.getArmature();
        this.bone = tokenArmature.getComponent(DragonbonesComponent).getBone('Plinko_Hand_4');
        this.plinkoToken = plinkoToken;
        this.tokenPreviousPosition = new Point(0, 0);
        this.delta = new Point(0, 0);

        const dropperState = dropperArmature.container.animation.getState('idle');
        let currentTime = dropperState.currentTime;

        const anim = firstDropAnimations.find(anim => anim.animation === dropAnimation);

        while (currentTime < anim.time) {
            currentTime = dropperState.currentTime;
            yield;
        }

        // Dropper 'drop' animation.
        setDropperAnimation(dropperArmature, 'drop');

        yield* delay(100);

        // Move token out of dropper bone.
        tokenContainer.addChild(this.plinkoToken);

        const tokenLocalPosition = this.plinkoToken.container.parent.toLocal(
            new Point(anim.startX, anim.startY),
            this.plinkoToken.container.parent.parent
        );

        // This should have been handled via `GameObject.addChild`.
        this.plinkoToken.container.position.copy(tokenLocalPosition);

        const initialY = this.plinkoToken.container.y;

        yield* this.playRootMotionAnimation(
            new DragonbonesDefault(tokenArmature, {
                animationName: anim.animation,
                playTimes: 1,
            }),
            initialY
        );

        for (let i = 0; i < path.length; i++) {
            const isLandingAnimation = i === path.length - 1;

            const animationName = isLandingAnimation
                ? getTokenLandAnimationName(path[i])
                : getTokenBounceAnimationName(path[i]);

            const animationClip = new DragonbonesDefault(tokenArmature, {
                animationName,
                playTimes: 1,
            });

            yield* this.playRootMotionAnimation(animationClip, initialY + 66.67 + i * 50);
        }
    }

    private *playRootMotionAnimation(animationClip: AnimationClip, startY: number = 0): IterableIterator<number> {
        animationClip.on(AnimationClipEvent.UPDATE, this.animationUpdate);

        const iterator = PlayAnimation(animationClip);

        // Force start Y position to account for progressive offsets between animations.
        this.plinkoToken.container.position.y = startY;

        // Yield once to start the animation.
        yield iterator.next().value;

        // Reset root motion positions to prevent position flickering at start of each animation.

        this.tokenPreviousPosition.set(0, 0);
        this.bone.offset.x = 0;
        this.bone.offset.y = 0;

        yield* iterator;
    }

    private animationUpdate = () => {
        // @ts-ignore
        const boneAnimationPose = this.bone.animationPose;
        this.delta.set(
            boneAnimationPose.x - this.tokenPreviousPosition.x,
            boneAnimationPose.y - this.tokenPreviousPosition.y
        );
        this.plinkoToken.container.x += this.delta.x;
        this.plinkoToken.container.y += this.delta.y;
        this.tokenPreviousPosition.set(boneAnimationPose.x, boneAnimationPose.y);
        this.bone.offset.x = boneAnimationPose.x * -1;
        this.bone.offset.y = boneAnimationPose.y * -1;
        this.bone.invalidUpdate();
    };
}
